import React from 'react';
import { ArrowUpDown, Filter, Search, X } from '../../../bravosIcons';
import { LibraryFilterPopover, type LibFilterOption } from '../components/LibraryFilterPopover';
import type { LibraryWorkoutPlansApi } from '../../hooks/useLibraryWorkoutPlans';
import { LIB_PLAN_SORT_OPTIONS } from '../../hooks/useLibraryWorkoutPlans';
import { PLAN_DURATION_BUCKET_LABELS, VALID_PLAN_DURATION_BUCKETS } from '../../types/library';
import type { WorkoutPlanDurationBucket } from '../../types/library';

type WorkoutPlansToolbarDesktopProps = {
  pl: LibraryWorkoutPlansApi;
  activeTab?: string;
};

/** Desktop Library — Workout Plans tab toolbar (§3). */
export function WorkoutPlansToolbarDesktop({ pl, activeTab }: WorkoutPlansToolbarDesktopProps) {
  if (activeTab != null && activeTab !== 'workoutPlans') return null;
  if (pl.libPlansEmptyVariant === 'true-empty') return null;

  return (
    <div className="lib-toolbar">
      <div className="lib-search">
        <Search />
        <input
          type="text"
          value={pl.workoutPlanNameFilter}
          onChange={(e) => pl.setWorkoutPlanNameFilter(e.target.value)}
          placeholder="Search plan name…"
          aria-label="Search workout plans by name"
        />
      </div>

      <div className="relative" ref={pl.libPlanDurationChipRef}>
        <button
          type="button"
          className={`lib-chip ${(pl.libPlanOpenChip === 'duration' || pl.libPlanDurationActive) ? 'is-active' : ''}`}
          onClick={() => pl.setLibPlanOpenChip(pl.libPlanOpenChip === 'duration' ? null : 'duration')}
          aria-haspopup="dialog"
          aria-expanded={pl.libPlanOpenChip === 'duration'}
        >
          <Filter />
          Duration
          {pl.libPlanDurationBuckets.size > 0 && (
            <span className="lib-chip-count-pill lib-tnum">{pl.libPlanDurationBuckets.size}</span>
          )}
        </button>
        {pl.libPlanOpenChip === 'duration' && (
          <LibraryFilterPopover
            options={VALID_PLAN_DURATION_BUCKETS.map<LibFilterOption>((b) => ({
              id: b,
              label: PLAN_DURATION_BUCKET_LABELS[b],
              matchCount: pl.libPlanDurationMatchCounts[b],
            }))}
            selected={pl.libPlanDurationBuckets as Set<string>}
            onChange={(next) => pl.setLibPlanDurationBuckets(next as Set<WorkoutPlanDurationBucket>)}
            onClose={() => pl.setLibPlanOpenChip(null)}
            ariaLabel="Duration filter"
          />
        )}
      </div>

      <span className="lib-chip-divider" aria-hidden="true" />

      <div className="relative" ref={pl.libPlanSortChipRef}>
        <button
          type="button"
          className={`lib-chip ${(pl.libPlanOpenChip === 'sort' || pl.libPlanSortShowsSelection) ? 'is-active' : ''}`}
          onClick={() => pl.setLibPlanOpenChip(pl.libPlanOpenChip === 'sort' ? null : 'sort')}
          aria-haspopup="listbox"
          aria-expanded={pl.libPlanOpenChip === 'sort'}
        >
          <ArrowUpDown />
          <span className="lib-sort-label">
            {pl.libPlanSortShowsSelection ? `Sort · ${pl.libPlanCurrentSortLabel}` : 'Sort'}
          </span>
        </button>
        {pl.libPlanOpenChip === 'sort' && (
          <div
            className="lib-popover lib-sort-popover"
            role="listbox"
            onKeyDown={(e) => { if (e.key === 'Escape') pl.setLibPlanOpenChip(null); }}
          >
            {LIB_PLAN_SORT_OPTIONS.map((opt) => (
              <label key={opt.id} className={`lib-popover-row ${pl.libPlanSort === opt.id ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name="lib-plan-sort"
                  checked={pl.libPlanSort === opt.id}
                  onChange={() => {
                    pl.setLibPlanSort(opt.id);
                    pl.setLibPlanSortExplicit(true);
                    pl.setLibPlanOpenChip(null);
                  }}
                />
                <span className="lib-popover-row-label">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {pl.libPlansHasActiveControls && (
        <button
          type="button"
          className="lib-clear-all-chip"
          onClick={pl.handleClearAllPlans}
          aria-label="Clear all filters, search, and sort"
        >
          <X />
          Clear all
          <span className="lib-clear-all-chip-count lib-tnum">{pl.libPlansAppliedCount}</span>
        </button>
      )}

      <div className="lib-results-meta">
        <strong className="lib-tnum">{pl.filteredAndSortedWorkoutPlans.length}</strong>{' '}
        {pl.filteredAndSortedWorkoutPlans.length === 1 ? 'result' : 'results'}
      </div>
    </div>
  );
}
